import { memo, PropsWithChildren, ReactNode } from 'react'

import { InfoBlock, InfoBlockRounded, InfoBlockType } from 'cng-common/components/infoBlock'
import { SimpleTable } from 'cng-common/components/simpleTable'
import { ReadOnlyField } from 'cng-common/features/form/components'
import { Translation } from 'cng-common/features/translations'
import { Nullable } from 'cng-common/models/utils'
import { Tooltip } from 'core-components/library/tooltip'
import { FieldGroup, FieldGroupColor } from 'shared/components/fieldGroup'

import { TSearchPortalContact } from '../../api'

import { contactDuplicateTranslation } from './translation'

import styles from './ContactDuplicate.module.scss'

interface Props {
  duplicate: Nullable<TSearchPortalContact>
  message?: ReactNode
  color?: FieldGroupColor
}

export const ContactDuplicate = memo<PropsWithChildren<Props>>(
  ({ duplicate, children, message = contactDuplicateTranslation.message, color }) => {
    if (!duplicate) {
      return null
    }

    return (
      <FieldGroup
        classNames={{
          groupFields: styles.groupFields,
        }}
        title={contactDuplicateTranslation.title}
        size={1}
        color={color}
      >
        {message ? (
          <InfoBlock text={message} type={InfoBlockType.Danger} rounded={InfoBlockRounded.Medium} />
        ) : null}
        <SimpleTable className={styles.table}>
          <SimpleTable.HeaderRow>
            {...Object.entries(contactDuplicateTranslation.fields).map(([key, column]) => (
              <SimpleTable.HeaderCell key={key}>
                <Translation text={column} span />
              </SimpleTable.HeaderCell>
            ))}
          </SimpleTable.HeaderRow>
          <SimpleTable.BodyRow>
            <SimpleTable.BodyCell>
              <div className={styles.field}>
                <Tooltip tipContent={() => <>{duplicate.locationId}</>}>
                  <ReadOnlyField value={duplicate.locationId} />
                </Tooltip>
              </div>
            </SimpleTable.BodyCell>
            <SimpleTable.BodyCell>
              <div className={styles.field}>
                <Tooltip tipContent={() => <>{duplicate.contactId}</>}>
                  <ReadOnlyField value={duplicate.contactId} />
                </Tooltip>
              </div>
            </SimpleTable.BodyCell>
            <SimpleTable.BodyCell>
              <div className={styles.field}>
                <Tooltip tipContent={() => <>{duplicate.emailAddress}</>}>
                  <ReadOnlyField value={duplicate.emailAddress} />
                </Tooltip>
              </div>
            </SimpleTable.BodyCell>
            <SimpleTable.BodyCell>
              <div className={styles.field}>
                <Tooltip tipContent={() => <>{duplicate.firstName}</>}>
                  <ReadOnlyField value={duplicate.firstName} />
                </Tooltip>
              </div>
            </SimpleTable.BodyCell>
            <SimpleTable.BodyCell>
              <div className={styles.field}>
                <Tooltip tipContent={() => <>{duplicate.lastName}</>}>
                  <ReadOnlyField value={duplicate.lastName} />
                </Tooltip>
              </div>
            </SimpleTable.BodyCell>
            <SimpleTable.BodyCell>
              <div className={styles.field}>
                <Tooltip tipContent={() => <>{duplicate.hpRoles.join(', ')}</>}>
                  <ReadOnlyField value={duplicate.hpRoles} />
                </Tooltip>
              </div>
            </SimpleTable.BodyCell>
            <SimpleTable.BodyCell>
              <div className={styles.field}>
                <Tooltip tipContent={() => <>{duplicate.partnerId}</>}>
                  <ReadOnlyField value={duplicate.partnerId} />
                </Tooltip>
              </div>
            </SimpleTable.BodyCell>
            <SimpleTable.BodyCell>
              <div className={styles.field}>
                <Tooltip tipContent={() => <>{duplicate.communicationName}</>}>
                  <ReadOnlyField value={duplicate.communicationName} />
                </Tooltip>
              </div>
            </SimpleTable.BodyCell>
            <SimpleTable.BodyCell>
              <div className={styles.field}>
                <Tooltip tipContent={() => <>{duplicate.status}</>}>
                  <ReadOnlyField value={duplicate.status} />
                </Tooltip>
              </div>
            </SimpleTable.BodyCell>
          </SimpleTable.BodyRow>
        </SimpleTable>

        {children}
      </FieldGroup>
    )
  }
)

ContactDuplicate.displayName = 'ContactDuplicate'
